/**
 * Shared CSV export utilities for DiscoRun.
 *
 * All public exports produce plain-text CSV and trigger a browser download.
 * The core primitive is `downloadCsv(rows, filename)` which accepts an array of
 * plain objects whose keys become column headers.
 */

// ---------------------------------------------------------------------------
// Core primitive
// ---------------------------------------------------------------------------

/**
 * Escape a cell value for CSV: wrap in double-quotes, escape internal quotes.
 */
export function escapeCell(value: unknown): string {
  if (value == null) return "";
  const str = String(value);
  // If the value contains a comma, newline, or double-quote, wrap in quotes
  if (str.includes(",") || str.includes("\n") || str.includes('"')) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
}

/**
 * Serialize an array of row-objects to a CSV string.
 * The column order follows the insertion order of the first row's keys.
 */
export function rowsToCsv(rows: Record<string, unknown>[]): string {
  if (rows.length === 0) return "";
  const headers = Object.keys(rows[0]);
  const lines: string[] = [headers.map(escapeCell).join(",")];
  for (const row of rows) {
    lines.push(headers.map((h) => escapeCell(row[h])).join(","));
  }
  return lines.join("\n");
}

/**
 * Trigger a browser download of a CSV string.
 */
export function downloadCsv(csvString: string, filename: string): void {
  const blob = new Blob([csvString], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.style.display = "none";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 100);
}

/** Convenience: build CSV from rows and immediately download. */
export function exportCsv(rows: Record<string, unknown>[], filename: string): void {
  downloadCsv(rowsToCsv(rows), filename);
}

// ---------------------------------------------------------------------------
// Today's date helper used in filenames
// ---------------------------------------------------------------------------
export function todayStr(): string {
  return new Date().toISOString().slice(0, 10);
}

// ---------------------------------------------------------------------------
// Domain-specific export functions
// ---------------------------------------------------------------------------

import type { PARInvoiceRow, VendorSummary } from "@/lib/par/csvInvoiceParser";
import type { BacktestInvoiceRow } from "@/types/backtest";
import type { PlatformUser } from "@/hooks/useUsers";

/** Minimal invoice shape used by InvoiceList and InvoiceGroupedList. */
export interface ExportableInvoice {
  id: string;
  payment_amount: number;
  payment_due_date: string;
  supplier_name: string;
  supplier_email: string;
  status: string;
  apr_rate: number;
  discount_amount: number | null;
  discounted_payment_amount: number | null;
  email_sent_at: string | null;
  email_error: string | null;
  created_at: string;
}

/**
 * Export the DiscoRun invoices table (Discounting tab).
 */
export function exportInvoicesCsv(invoices: ExportableInvoice[], filename?: string): void {
  const rows = invoices.map((inv) => ({
    "Supplier Name": inv.supplier_name,
    "Supplier Email": inv.supplier_email,
    "Payment Amount": inv.payment_amount,
    "Payment Due Date": inv.payment_due_date,
    Status: inv.status,
    "APR Rate (%)": inv.apr_rate,
    "Discount Amount": inv.discount_amount ?? "",
    "Discounted Payment": inv.discounted_payment_amount ?? "",
    "Email Sent At": inv.email_sent_at ?? "",
    "Email Error": inv.email_error ?? "",
    "Created At": inv.created_at,
  }));
  exportCsv(rows, filename ?? `invoices-${todayStr()}.csv`);
}

/**
 * Export the PAR Outstanding Invoice table.
 */
export function exportOutstandingInvoicesCsv(
  rows: Array<{
    vendor_name: string;
    vendor_number?: string;
    invoice_number?: string;
    invoiced_date?: string;
    due_date?: string;
    early_pay_due_date?: string;
    outstanding: number;
    potential_savings: number;
    terms?: string;
    discount_pct?: number | null;
  }>,
  filename?: string
): void {
  const csvRows = rows.map((r) => ({
    Vendor: r.vendor_name,
    "Vendor #": r.vendor_number ?? "",
    "Invoice #": r.invoice_number ?? "",
    "Invoiced Date": r.invoiced_date ?? "",
    "Due Date": r.due_date ?? "",
    "Early Pay Due Date": r.early_pay_due_date ?? "",
    "Outstanding Amount": r.outstanding.toFixed(2),
    "Potential Savings": r.potential_savings.toFixed(2),
    "Payment Terms": r.terms ?? "",
    "Discount %": r.discount_pct != null ? r.discount_pct.toString() : "",
  }));
  exportCsv(csvRows, filename ?? `outstanding-invoices-${todayStr()}.csv`);
}

/**
 * Export the PAR Vendor Summary table.
 */
export function exportVendorSummaryCsv(vendors: VendorSummary[], filename?: string): void {
  const rows = vendors.map((v) => ({
    Vendor: v.vendor_name,
    "Vendor #": v.vendor_number,
    "Invoice Count": v.invoice_count,
    "Total Invoiced": v.total_invoiced.toFixed(2),
    "Total Paid": v.total_paid.toFixed(2),
    "Total Discounts": v.total_discount.toFixed(2),
    "Avg Days Paid Early": v.avg_days_paid_early.toFixed(1),
    "Terms Used": v.terms_used.join("; "),
    "Earliest Invoice Date": v.earliest_invoice_date ?? "",
    "Latest Invoice Date": v.latest_invoice_date ?? "",
    "Earliest Due Date": v.earliest_due_date ?? "",
    "Latest Due Date": v.latest_due_date ?? "",
    "Total Potential Savings": v.total_potential_savings.toFixed(2),
    "Best Discount %": v.best_discount_pct != null ? v.best_discount_pct.toString() : "",
    "Opportunity ($)": v.opportunity > 0 ? v.opportunity.toFixed(2) : "0.00",
    "Opportunity Uplift (%)":
      v.opportunity > 0 && v.total_potential_savings > 0
        ? (v.opportunity_pct * 100).toFixed(1)
        : "",
  }));
  exportCsv(rows, filename ?? `vendor-summary-${todayStr()}.csv`);
}

/**
 * Export backtest invoice rows.
 */
export function exportBacktestRowsCsv(rows: BacktestInvoiceRow[], filename?: string): void {
  const csvRows = rows.map((r) => ({
    "#": r.row_number,
    Supplier: r.supplier_name ?? "",
    "Invoice #": r.invoice_number ?? "",
    "Invoice Date": r.invoice_date ?? "",
    Amount: r.invoice_amount != null ? r.invoice_amount.toFixed(2) : "",
    "Payment Terms": r.payment_terms ?? "",
    "Payment Date": r.actual_payment_date ?? "",
    Deadline: r.discount_deadline ?? "",
    Status: r.parse_error
      ? "Error"
      : !r.was_eligible
        ? "No Terms"
        : r.discount_captured
          ? "Captured"
          : "Missed",
    "Missed Savings": r.missed_savings > 0 ? r.missed_savings.toFixed(2) : "",
    "Captured Savings": r.captured_savings > 0 ? r.captured_savings.toFixed(2) : "",
    "Parse Error": r.parse_error ?? "",
  }));
  exportCsv(csvRows, filename ?? `backtest-rows-${todayStr()}.csv`);
}

/**
 * Export backtest supplier breakdown.
 */
export function exportBacktestSuppliersCsv(rows: BacktestInvoiceRow[], filename?: string): void {
  // Build supplier rollup (mirrors SupplierBreakdownTable logic)
  const bySupplier: Record<
    string,
    {
      supplierName: string;
      eligible: number;
      missed: number;
      captured: number;
      missedSavings: number;
      capturedSavings: number;
    }
  > = {};
  for (const row of rows) {
    if (!row.was_eligible) continue;
    const name = row.supplier_name ?? "Unknown";
    if (!bySupplier[name]) {
      bySupplier[name] = {
        supplierName: name,
        eligible: 0,
        missed: 0,
        captured: 0,
        missedSavings: 0,
        capturedSavings: 0,
      };
    }
    bySupplier[name].eligible++;
    if (row.discount_captured) {
      bySupplier[name].captured++;
      bySupplier[name].capturedSavings += row.captured_savings;
    } else {
      bySupplier[name].missed++;
      bySupplier[name].missedSavings += row.missed_savings;
    }
  }
  const csvRows = Object.values(bySupplier)
    .sort((a, b) => b.missedSavings - a.missedSavings)
    .map((s) => ({
      Supplier: s.supplierName,
      "Eligible Invoices": s.eligible,
      "Missed Count": s.missed,
      "Captured Count": s.captured,
      "Missed Savings": s.missedSavings.toFixed(2),
      "Captured Savings": s.capturedSavings.toFixed(2),
      "Capture Rate (%)": s.eligible > 0 ? ((s.captured / s.eligible) * 100).toFixed(1) : "0",
    }));
  exportCsv(csvRows, filename ?? `backtest-suppliers-${todayStr()}.csv`);
}

/**
 * Export the PAR Vendor Analysis table (InvoiceDetailTable).
 */
export function exportPARVendorAnalysisCsv(rows: PARInvoiceRow[], filename?: string): void {
  const csvRows = rows.map((r) => ({
    Vendor: r.vendor_name,
    "Vendor #": r.vendor_number ?? "",
    "Invoice #": r.invoice_number ?? "",
    Description: r.description ?? "",
    Status: r.status ?? "",
    "Payment Status": r.payment_status ?? "",
    Terms: r.terms ?? "",
    "Invoiced Date": r.invoiced_date ?? "",
    "Due Date": r.due_date ?? "",
    "Early Pay Due Date": r.early_pay_due_date ?? "",
    "Paid Date": r.paid_date ?? "",
    "Amount Invoiced": r.amount_invoiced != null ? r.amount_invoiced.toFixed(2) : "",
    "Amount Paid": r.amount_paid != null ? r.amount_paid.toFixed(2) : "",
    "Amount Discount": r.amount_discount != null ? r.amount_discount.toFixed(2) : "",
    "Discount %": r.discount_pct != null ? r.discount_pct.toString() : "",
    "Potential Savings": r.potential_savings != null ? r.potential_savings.toFixed(2) : "",
    "Days Paid Early": r.days_paid_early != null ? r.days_paid_early.toString() : "",
    Year: r.invoice_year != null ? r.invoice_year.toString() : "",
  }));
  exportCsv(csvRows, filename ?? `vendor-analysis-${todayStr()}.csv`);
}

/**
 * Export the User Management page users list.
 */
export function exportUsersCsv(users: PlatformUser[], filename?: string): void {
  const rows = users.map((u) => ({
    Name: u.name,
    Email: u.email,
    Role: u.role,
    Status: u.status,
    "Last Sign In": u.last_sign_in_at ? new Date(u.last_sign_in_at).toLocaleDateString() : "",
  }));
  exportCsv(rows, filename ?? `users-${todayStr()}.csv`);
}
